import { useCallback } from 'react';

import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useAppDispatch } from '@/store';
import { useGetOrganizationCreditsQuery } from '@/store/features/extension.feature';
import { openCreditGate } from '@/store/features/ui.feature';

/** Opens the credit dialog from a Clu card that costs more than the balance. */
export function useBuyCredits(needed?: number) {
  const dispatch = useAppDispatch();
  return useCallback(
    () => dispatch(openCreditGate({ action: 'Clu', needed })),
    [dispatch, needed]
  );
}

/**
 * The balance a card checks its cost against: the higher of the one the
 * server sent with the card and the org's live balance. A top-up after the
 * card was shown unblocks it at once, and the live figure (which may leave
 * out this turn's own hold) never blocks more than the server's does.
 */
export function useCardBalance(
  saved: number | null | undefined
): number | null {
  const { organizationId } = useFeatureAccess();
  const { currentData } = useGetOrganizationCreditsQuery(
    { organizationId },
    { skip: !organizationId || saved == null }
  );
  if (saved == null || !Number.isFinite(saved)) return null;
  const live = currentData?.count;
  return typeof live === 'number' && Number.isFinite(live) && live > saved
    ? live
    : saved;
}
